/**
 * 成片总时长的**单一真源**(2026-07-29)。
 *
 * 病灶(测试反馈 bug 9):同一个页面,右上徽标显示 5s、右侧积分却按 15s 算 ——
 * 「改动时长右侧积分会随之改变,但是右上的 5s 不变」。因为这一个概念此前有**两个**算法:
 *   - 徽标走 VideoCreatePage.effectiveDurationSec:多镜取 panelDurations 之和,
 *     **否则直接取 form.durationSec(默认 5)**,压根不看用户选的时长档;
 *   - 积分走 useVideoCreditEstimate:未编排时**优先**用时长档目标秒数(15/30/45)。
 *
 * 连带 bug 5「两个时长只有下面的有效果」也是这里没讲清:上面的时长档(briefDuration)
 * 才是真生效的那个(进导演 prompt + ai 侧 `_apply_duration_tier` 硬钳「说 15s 就 15s」),
 * 下面高级选项的 durationSec 在**多镜时根本不用**(提交时只当每镜兜底,见
 * VideoCreatePage 的 isMultiShot 分支),界面却把它做成可编辑输入框还拿它显徽标 → 反着教用户。
 *
 * 口径(与 ai-service 一致,优先级从高到低):
 *   1. 已编排多镜 → Σ(panelDurations),这是**真的**会渲染出来的秒数;
 *   2. 用户选了时长档 → 该档目标秒数(ai 会把 Σ 精确钳到它);
 *   3. 都没有 → form.durationSec(单镜路的每镜时长)。
 */
import type { FormState } from "./videoCreateTypes";

/**
 * 时长档 → 目标总秒数。与 VideoBriefPanel 的「短·15s / 中·30s / 长·45s」及
 * ai-service `grid_detect._TIER_TARGET_SEC` 同源。
 * ★中英都收:军师链与 H5 创建页传的是英文 tier —— ai 侧曾因只认中文键让硬钳在两条主链上
 * 从未生效(实证 45s 档只出 37s),前端别再犯同一个错。
 */
export const BRIEF_DURATION_SEC: Record<string, number> = {
  短: 15,
  short: 15,
  中: 30,
  medium: 30,
  长: 45,
  long: 45,
  // ★0826 上架 60s 档:引擎侧每个目的都有**真** beats_60(不是把 45s 拉长),
  // shot_budget 天花板已从 16 抬到 24。与 ai `intent_infer._TIER_TARGET_SEC` 同源。
  超长: 60,
  extra_long: 60,
};

/**
 * 目的流(选了目的卡 / 页内点目的 chip)的默认时长档(B2 #11,0927)。
 * ★此前缺省停在「AI 按内容定」(briefDuration 缺省)—— K4 实测一点编排就出 30s · 6 镜的单;
 *   手册演示路径也是「短 · 15s」。用户主动点「AI 按内容定」是 null(不是缺省),不会被这条覆盖。
 */
export const DEFAULT_BRIEF_DURATION = "短";

export function briefTargetSec(briefDuration?: string | null): number | undefined {
  const key = (briefDuration ?? "").trim();
  return key ? BRIEF_DURATION_SEC[key] : undefined;
}

/** 已编排多镜(≥2 镜)—— 此时 panelDurations 才是真会渲染的秒数。 */
export function isArrangedMultiShot(form: Pick<FormState, "panelDurations">): boolean {
  const pd = form.panelDurations;
  return !!(pd && pd.length >= 2);
}

/**
 * 成片总时长(秒)。徽标 / 高级选项摘要 / 积分估算**三处必须都用它** ——
 * 任何一处自己另算一遍,就是 bug 9 那种「积分变了数字没变」。
 */
export function effectiveDurationSec(
  form: Pick<FormState, "panelDurations" | "durationSec" | "briefDuration">
): number {
  if (isArrangedMultiShot(form)) {
    return (form.panelDurations ?? []).reduce((a, b) => a + (b || 0), 0);
  }
  return briefTargetSec(form.briefDuration) ?? form.durationSec;
}

/**
 * 单镜(直发)一次 provider 调用的时长上限 —— **跟模型走**(0824 用户实测:不走编排只能出
 * 15s,而 Seedance 2.5 单次上限是 30s)。真值 = capability.durationRange[1](backend SOT,
 * ai 侧 clamp_duration 同源);拿不到能力表时保守回落 15(2.0 时代的通用上限)。
 */
export const BARE_SEND_FALLBACK_MAX_SEC = 15;

export function bareSendMaxSec(durationRange?: readonly number[] | null): number {
  const hi = durationRange?.[1];
  return typeof hi === "number" && hi > 0 ? hi : BARE_SEND_FALLBACK_MAX_SEC;
}

/**
 * 「不编排,直接出片」真正会发出去的秒数 —— **组包 / 积分预估 / 确认弹窗三处共用**。
 *
 * ★2026-08-11 建(测试反馈 E-14a,爱民「选的30秒,出来的只有5秒」):
 *   在此之前,这个概念在三处各算各的 ——
 *     · buildTaskRequest 发的是 `form.durationSec`(默认 5,压根不看时长档);
 *     · useVideoCreditEstimate 的 bareSend 也按 `form.durationSec` 算;
 *     · 而页面右上的徽标按 `effectiveDurationSec`(选了档就是 30)显示。
 *   于是「界面显示 30s、实际出 5s」。这正是 bug 9「改时长积分变了、右上 5s 不变」的翻版:
 *   同一个概念散在多处各写一遍,迟早对不上。
 *
 * ★为什么要钳:直发就是一次 provider 调用,单镜上限 15s。选了「中·30s」走直发,
 *   拿到的**必然**不是 30s —— 这是这条路的固有限制。钳到能给的最长,并由弹窗明说差异。
 *   钳位与告知缺一不可:只钳不说,用户下次仍会以为选了 30 就是 30。
 */
export function bareSendDurationSec(
  form: Pick<FormState, "panelDurations" | "durationSec" | "briefDuration">,
  maxSec: number = BARE_SEND_FALLBACK_MAX_SEC
): number {
  return Math.min(maxSec, Math.max(2, effectiveDurationSec(form)));
}

/** 直发时用户选的时长档是否会被钳短(true 时确认弹窗必须说明,否则用户以为选了 30 就是 30)。 */
export function bareSendClampsTier(
  form: Pick<FormState, "panelDurations" | "durationSec" | "briefDuration">,
  maxSec: number = BARE_SEND_FALLBACK_MAX_SEC
): boolean {
  const tier = briefTargetSec(form.briefDuration);
  return tier !== undefined && tier > maxSec;
}

/**
 * 高级选项里的「单镜时长」输入框此刻是否只读。
 *
 * 只读的两种情形 —— 都不是它说了算:
 *   - 已编排多镜:真时长是各镜之和(改这个值渲染时也只当兜底);
 *   - 用户选了时长档:ai 会把 Σ 精确钳到档位目标,这里填什么都会被覆盖。
 * 可编辑只剩「单镜 + 没选档」这一种,那才是它真正生效的场景。
 */
export function isPerShotDurationLocked(
  form: Pick<FormState, "panelDurations" | "briefDuration">
): boolean {
  return isArrangedMultiShot(form) || briefTargetSec(form.briefDuration) !== undefined;
}

/**
 * 导演交代卡「预计成片 Xs」显示的秒数(0926 修)。
 *
 * ★演示机实测(「AI 按内容定」r2):同一页交代卡写「预计成片 23s · 6 镜」、分镜条写「总长 30s · 6 镜」,
 *   实际成片 30s、按 30s 扣费。23 是导演产的 `estimatedDurationSec`(编排前的估计),30 是各镜
 *   `panelDurations` 之和 —— 后者才是真会渲染、真会计费的秒数(与 `effectiveDurationSec`、出片条
 *   报价同源)。所以已编排多镜时一律显示 Σ;只有还没排出多镜时才退回导演的估计。
 */
export function briefShownDurationSec(
  form: Pick<FormState, "panelDurations" | "durationSec" | "briefDuration" | "estimatedDurationSec">
): number | null {
  if (isArrangedMultiShot(form)) return effectiveDurationSec(form);
  return form.estimatedDurationSec ?? null;
}
